let notificationAudio = null;
let ringAudio = null;
let ringShouldBePlaying = false;
let activeRingSoundUrl = null;
let audioUnlockListenersAttached = false;
const MESSENGER_NOTIFICATION_TAG = "teamtools-messenger-message";

export function resolveMessengerNotificationSoundUrl(messengerIframeUrl) {
  try {
    return new URL("/sounds/message.mp3", window.location.origin).href;
  } catch (_error) {
    // Fall back to the messenger iframe asset below.
  }

  if (!messengerIframeUrl) {
    return null;
  }

  try {
    return new URL("/media/message.mp3", messengerIframeUrl).href;
  } catch (_error) {
    return null;
  }
}

export function resolveMessengerRingSoundUrl(messengerIframeUrl) {
  if (!messengerIframeUrl) {
    return null;
  }

  try {
    return new URL("/media/ring.mp3", messengerIframeUrl).href;
  } catch (_error) {
    return null;
  }
}

function getNotificationAudio(soundUrl) {
  if (!notificationAudio || notificationAudio.src !== soundUrl) {
    notificationAudio = new Audio(soundUrl);
    notificationAudio.preload = "auto";
    notificationAudio.crossOrigin = "anonymous";
  }

  return notificationAudio;
}

function getRingAudio(soundUrl) {
  if (!ringAudio || ringAudio.src !== soundUrl) {
    ringAudio = new Audio(soundUrl);
    ringAudio.preload = "auto";
    ringAudio.loop = true;
    ringAudio.setAttribute("playsinline", "");
  }

  return ringAudio;
}

function markRingAsMediaPlayback() {
  if (!("mediaSession" in navigator)) {
    return;
  }

  try {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: "Входящий звонок",
      artist: "Мессенджер",
    });
    navigator.mediaSession.playbackState = "playing";
  } catch (_error) {
    // Media Session is only a best-effort hint for mobile browsers.
  }
}

function ensureRingPlayback() {
  if (!ringShouldBePlaying || !activeRingSoundUrl) {
    return;
  }

  try {
    const audio = getRingAudio(activeRingSoundUrl);
    audio.loop = true;
    audio.volume = 1;
    const playPromise = audio.play();
    if (playPromise?.catch) {
      playPromise.catch((error) => {
        // The initial user-gesture unlock remains the fallback for autoplay policy.
        console.warn("messenger ring play failed:", error?.name || error);
      });
    }
    markRingAsMediaPlayback();
  } catch (error) {
    // Background playback is best effort on mobile browsers.
    console.warn("messenger ring play failed:", error?.name || error);
  }
}

function tryUnlockNotificationAudio(soundUrl) {
  if (!soundUrl) {
    return;
  }

  try {
    const audio = getNotificationAudio(soundUrl);
    const previousVolume = audio.volume;
    audio.volume = 0;
    const playPromise = audio.play();
    if (playPromise?.then) {
      playPromise
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = previousVolume || 1;
        })
        .catch(() => {
          audio.volume = previousVolume || 1;
        });
    } else {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = previousVolume || 1;
    }
  } catch (_error) {
    // Mobile browsers may block autoplay until a user gesture.
  }
}

function tryUnlockRingAudio(soundUrl) {
  if (!soundUrl) {
    return;
  }

  try {
    const audio = getRingAudio(soundUrl);
    const previousVolume = audio.volume;
    audio.loop = false;
    audio.volume = 0;
    const playPromise = audio.play();
    if (playPromise?.then) {
      playPromise
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = previousVolume || 1;
          audio.loop = true;
        })
        .catch(() => {
          audio.volume = previousVolume || 1;
          audio.loop = true;
        });
    } else {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = previousVolume || 1;
      audio.loop = true;
    }
  } catch (_error) {
    // Mobile browsers may block autoplay until a user gesture.
  }
}

export function initMessengerNotificationSoundUnlock(soundUrl, ringSoundUrl) {
  if ((!soundUrl && !ringSoundUrl) || audioUnlockListenersAttached) {
    return undefined;
  }

  const unlock = () => {
    tryUnlockNotificationAudio(soundUrl);
    tryUnlockRingAudio(ringSoundUrl);
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("keydown", unlock, true);
    audioUnlockListenersAttached = false;
  };

  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("keydown", unlock, true);
  audioUnlockListenersAttached = true;

  return () => {
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("keydown", unlock, true);
    audioUnlockListenersAttached = false;
  };
}

export function playMessengerNotificationSound(soundUrl) {
  if (!soundUrl) {
    return;
  }

  try {
    const audio = getNotificationAudio(soundUrl);
    audio.loop = false;
    audio.volume = 1;
    audio.currentTime = 0;
    const playPromise = audio.play();
    if (playPromise?.catch) {
      playPromise.catch((error) => {
        // Autoplay policy may block playback until unlock runs.
        console.warn("messenger notification play failed:", error?.name || error);
      });
    }
  } catch (error) {
    console.warn("messenger notification play failed:", error?.name || error);
  }
}

export function playMessengerCallRing(soundUrl) {
  if (!soundUrl) {
    return;
  }

  try {
    activeRingSoundUrl = soundUrl;
    ringShouldBePlaying = true;
    const audio = getRingAudio(soundUrl);
    audio.loop = true;
    audio.volume = 1;
    audio.currentTime = 0;
    ensureRingPlayback();
  } catch (error) {
    console.warn("messenger ring play failed:", error?.name || error);
  }
}

export function stopMessengerCallRing() {
  ringShouldBePlaying = false;
  activeRingSoundUrl = null;

  if (!ringAudio) {
    return;
  }

  try {
    ringAudio.pause();
    ringAudio.currentTime = 0;
    if ("mediaSession" in navigator) {
      navigator.mediaSession.playbackState = "none";
      navigator.mediaSession.metadata = null;
    }
  } catch (_error) {
    // Ignore stop errors.
  }
}

// Mobile browsers may suspend an iframe or audio element while changing tabs.
// Reassert the already user-unlocked playback at each lifecycle transition.
document.addEventListener("visibilitychange", ensureRingPlayback);
window.addEventListener("pagehide", ensureRingPlayback);
window.addEventListener("pageshow", ensureRingPlayback);

export function isMessengerBackgroundNotificationSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

export async function ensureMessengerNotificationPermission() {
  if (!isMessengerBackgroundNotificationSupported()) {
    return "unsupported";
  }

  if (
    Notification.permission === "granted" ||
    Notification.permission === "denied"
  ) {
    return Notification.permission;
  }

  try {
    return await Notification.requestPermission();
  } catch (_error) {
    return "denied";
  }
}

export function showMessengerBackgroundNotification({
  title,
  body,
  icon,
  onClick,
}) {
  if (!isMessengerBackgroundNotificationSupported()) {
    return null;
  }

  if (Notification.permission !== "granted") {
    return null;
  }

  const notificationTitle = title || "Мессенджер";
  const notification = new Notification(notificationTitle, {
    body: body || "",
    icon: icon || `${window.location.origin}/logo.svg`,
    tag: MESSENGER_NOTIFICATION_TAG,
    renotify: true,
  });

  notification.onclick = () => {
    window.focus();
    onClick?.();
    notification.close();
  };

  return notification;
}

export function handleMessengerIncomingNotification({
  soundUrl,
  ringSoundUrl,
  payload,
  onNotificationClick,
  enableBackgroundNotifications = false,
}) {
  if (!payload) {
    return;
  }

  if (payload.stopSound) {
    stopMessengerCallRing();
    return;
  }

  const shouldPlaySound = Boolean(payload.playSound);
  const shouldLoop = Boolean(payload.loop);
  const isIncomingCall = Boolean(payload.incomingCall) || shouldLoop;
  // Prefer Element-provided URL; for calls fall back to ring.mp3, not message beep.
  const effectiveSoundUrl =
    payload.soundUrl ||
    (isIncomingCall ? ringSoundUrl || soundUrl : soundUrl);

  if (document.hidden) {
    if (enableBackgroundNotifications) {
      showMessengerBackgroundNotification({
        title: payload.title,
        body: payload.body,
        icon: payload.icon,
        onClick: onNotificationClick,
      });
    }
    if (shouldPlaySound) {
      if (shouldLoop) {
        playMessengerCallRing(effectiveSoundUrl);
      } else {
        playMessengerNotificationSound(effectiveSoundUrl);
      }
    }
    return;
  }

  if (shouldPlaySound) {
    if (shouldLoop) {
      playMessengerCallRing(effectiveSoundUrl);
    } else {
      playMessengerNotificationSound(effectiveSoundUrl);
    }
  }
}
